// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Data hooks for the alarm-operations surfaces.

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Alarm, AlarmBucket, AuditEntry, FloorPlan, LogNote } from '../types/alarm';
import type { CriticalSystems, FloorPlanLayout } from '../types/floorPlan';
import {
  CRITICAL_SYSTEMS_UNPUBLISHED,
  PLAN_GEOMETRY_UNPUBLISHED,
} from '../components/floor-plan/missingData';
import type { AlarmApiRecord } from '../types/alarmApi';
import type { SiteSummary } from '../types/me';
import * as alarmsService from '../services/alarmsService';
import * as opsDataService from '../services/opsDataMockService';
import { getAlarm, listAlarmsForSites } from '../services/alarmApiService';
import type { GetAlarmNarrowing } from '../services/alarmApiService';
import { enrichRecordToAlarm } from '../services/alarmEnrichment';
import { MOCK_ALARM_RECORDS } from '../mocks/alarms';
import { isClosedState } from '../types/alarmFsm';
import { queryKeys } from '../lib/queryKeys';

/**
 * True when the AEM record represents an alarm still requiring attention.
 *
 * CLOSED is the only resolved state (agreed Oct 26): cleared alarms still wait
 * for an operator to close them. SHELVED_* and WO_* (retired by helios-aem #134
 * but still read until the remap runs) are live too, and a shelved alarm in
 * particular is suppressed rather than resolved.
 */
export function isAlarmOpen(record: AlarmApiRecord): boolean {
  return !isClosedState(record.alarm_status);
}

/** Narrow the seeded records the same way the API filters do (see getAlarm). */
function findMockAlarmRecord(
  siteId: string,
  alarmCode: string,
  narrowing: GetAlarmNarrowing
): AlarmApiRecord | null {
  const records = MOCK_ALARM_RECORDS(siteId);
  const { alarmId, equipmentId } = narrowing;
  if (alarmId) return records.find(r => r.alarm_id === alarmId) ?? null;
  if (equipmentId) {
    const match = records.find(r => r.alarm_code === alarmCode && r.equipment_id === equipmentId);
    if (match) return match;
  }
  return records.find(r => r.alarm_code === alarmCode) ?? null;
}

/**
 * Resolve a single alarm for the detail page — from the live AEM API, or from
 * the same locally-seeded records the alarm list falls back to when
 * REACT_APP_FORCE_ALARM_MOCKED_DATA=true (see alarmsService.shouldMockAlarms).
 *
 * `alarmCode` is the RULE, so it is not on its own an identity — the narrowing
 * carries the `alarm_id`/`equipment_id` the detail route was linked with, which
 * is what keeps two alarms sharing a code from resolving to the same row.
 *
 * Rule + SOP enrichment is shared with the anomaly detail page (findings are the
 * same row shape upstream) — see services/alarmEnrichment.ts for how severity is
 * derived and how a failed SOP degrades.
 */
async function loadAlarmWithSop(
  siteId: string,
  alarmCode: string,
  narrowing: GetAlarmNarrowing = {}
): Promise<Alarm | null> {
  const mocked = alarmsService.shouldMockAlarms();
  const record = mocked
    ? findMockAlarmRecord(siteId, alarmCode, narrowing)
    : await getAlarm(siteId, alarmCode, narrowing);
  if (!record) return null;

  return enrichRecordToAlarm(siteId, record, {
    skipRuleLookup: mocked,
    logTag: 'useAlarms',
  });
}

interface AsyncResult<T> {
  data: T;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function useAsync<T>(loader: () => Promise<T>, initial: T, deps: unknown[]): AsyncResult<T> {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await loader();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // loader identity intentionally excluded; deps describe the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, fetchKey]);

  return { data, loading, error, refetch };
}

/**
 * Fetch open alarms across every site in the fleet.
 *
 * The AEM API is per-site; this fans out one request per site and merges the
 * results. Records with alarm_status of 'closed' or 'cleared' are excluded.
 * Until the API carries a severity field all records should be treated as P1
 * by consumers.
 */
export function useFleetAlarms(sites: SiteSummary[]) {
  const scopeKey = sites.map(s => s.site_id).join(',');

  const result = useAsync<{ records: AlarmApiRecord[]; failedSiteIds: string[] }>(
    () => {
      if (sites.length === 0) return Promise.resolve({ records: [], failedSiteIds: [] });
      const scopes = sites.map(s => ({ siteId: s.site_id, organizationId: s.org_id }));
      return listAlarmsForSites(scopes).then(({ records, failedSiteIds }) => ({
        records: records.filter(isAlarmOpen),
        failedSiteIds,
      }));
    },
    { records: [], failedSiteIds: [] },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey]
  );
  return {
    records: result.data?.records,
    failedSiteIds: result.data?.failedSiteIds ?? [],
    ...result,
  };
}

/**
 * One alarm for the detail page.
 *
 * The only hook in this file on TanStack Query rather than the local `useAsync`
 * (ADR-0013): a write to an alarm has to invalidate the row the operator is
 * looking at as well as the lists it appears in, and `useApplyAlarmEvent` can
 * only do that if there is a cache entry to invalidate. No `refetchInterval` —
 * a detail page is read, not watched, and re-reading under the operator would
 * churn the SOP check-off state keyed off `alarm`.
 *
 * Returns the `{ data, loading, error, refetch }` shape the rest of this file
 * uses, so callers didn't have to change with the storage.
 */
export function useAlarm(
  id: string | undefined,
  siteId: string | undefined,
  equipmentId?: string,
  alarmId?: string
) {
  const query = useQuery({
    queryKey: queryKeys.alarm(siteId, id, equipmentId, alarmId),
    queryFn: () => loadAlarmWithSop(siteId as string, id as string, { alarmId, equipmentId }),
    enabled: !!id && !!siteId,
  });

  const alarm = query.data ?? null;
  return {
    ...query,
    alarm,
    data: alarm,
    loading: query.isLoading,
    error: query.error ? (query.error.message ?? 'Failed to load') : null,
    refetch: query.refetch,
  };
}

export function useBucketCounts(siteId?: string) {
  const result = useAsync<Record<AlarmBucket, number>>(
    () => alarmsService.getBucketCounts(siteId),
    { critical: 0, high: 0, events: 0, closed: 0 },
    [siteId]
  );
  return { counts: result.data, ...result };
}

export function useAudit(params: opsDataService.AuditQuery = {}) {
  const { query, actor, sort } = params;
  const result = useAsync<AuditEntry[]>(
    () => opsDataService.getAuditEntries({ query, actor, sort }),
    [],
    [query, actor, sort]
  );
  return { entries: result.data, ...result };
}

export function useLog(query?: string) {
  const result = useAsync<LogNote[]>(() => opsDataService.getLogNotes(query), [], [query]);
  return { notes: result.data, ...result };
}

export function useFloorPlan(siteId: string) {
  const result = useAsync<FloorPlan | null>(() => opsDataService.getFloorPlan(siteId), null, [
    siteId,
  ]);
  return { floorPlan: result.data, ...result };
}

/**
 * Critical Systems & Plant readouts under the Floor Plan graphic.
 *
 * **No endpoint serves this.** The seeded stand-in that used to back it has
 * been removed rather than left to read as real plant telemetry. The hook stays
 * as the seam: when the endpoint lands it fetches, adapts the response through
 * `criticalSystemsResponseToView` in `services/floorPlanMappers`, and nothing
 * above it changes. Until then it reports the gap so the screen can name it in
 * the position each readout would have occupied.
 */
export function useCriticalSystems(_siteId: string) {
  return {
    criticalSystems: null as CriticalSystems | null,
    loading: false,
    error: null,
    unavailable: CRITICAL_SYSTEMS_UNPUBLISHED,
  };
}

/**
 * Plan geometry backing the Floor Plan graphic. **No endpoint serves this**, and
 * unlike the readouts above there is no contract that could: the ontology models
 * `hasLocation` / `isLocationOf` / `containsEquipment`, so the twin knows which
 * room a unit sits in, but carries no coordinate, dimension or extent anywhere.
 * A drawing needs geometry from CAD/BIM, which nothing publishes.
 *
 * Same seam as above: `floorPlanResponseToLayout` is the adapter to call when a
 * source exists.
 */
export function useFloorPlanLayout(_siteId: string) {
  return {
    layout: null as FloorPlanLayout | null,
    loading: false,
    error: null,
    unavailable: PLAN_GEOMETRY_UNPUBLISHED,
  };
}
